/**
 * Account-switch session orchestration (Gordon 2026-07-20).
 *
 * Two responsibilities, both deliberately isolated from React so they are unit-
 * testable and so the CACHE-FLUSH proof is one auditable function:
 *
 *  1. validateAccountToken() — a GUARDED probe of a remembered account's stored
 *     token. It uses a FRESH axios (NOT src/services/request.js's shared instance),
 *     so a 401 for the *target* account can never fire the shared response
 *     interceptor that wipes the *current* account's keyCloak + redirects
 *     (request.js:63-74). Probing B must never sign out A.
 *
 *  2. flushSessionCaches() + switchToAccount() — the no-cross-account-data-bleed
 *     guarantee. Because most redux reducers do NOT handle LOGOUT (only the
 *     userProfile slice does — src/redux/reducers/user.js:47), the ONLY provably
 *     clean hand-off is to flush every in-memory + persisted cache and then HARD
 *     RELOAD into the new account: a full document load recreates the redux store,
 *     the react-query client, and every React context from scratch — nothing from
 *     account A can survive it.
 */
import axios from "axios";
import { API_BASE_PATH, API_PATH } from "../utils/config";
import { upsertAccount } from "./accountRegistry";

/** localStorage keys that are ACCOUNT-scoped and must not carry across a switch. */
export const KEYCLOAK_KEY = "keyCloak";
export const REDUX_PERSIST_KEY = "persist:root"; // redux-persist key "root" (src/redux/index.js)
export const ACCOUNT_SCOPED_LS_KEYS = [
  REDUX_PERSIST_KEY,
  "AITokenLeft", // per-account AI credit balance (utils/localStorageHelper.js)
  "show_walkthrough", // set at login (Login/index.jsx)
];
export const ACCOUNT_SCOPED_SS_KEYS = ["referralData"];

/**
 * Is this remembered account's stored token still accepted by the API?
 * Resolves true only on a 2xx from a lightweight authenticated GET. Any
 * 401/403/expired/network error resolves false (→ caller drops to a prefilled
 * login instead of switching). Never throws; never touches the current session.
 */
export async function validateAccountToken({ token, userId, request } = {}) {
  if (!token || userId == null) return false;
  const doGet = request || ((url, cfg) => axios.get(url, cfg));
  try {
    const url = `${API_BASE_PATH || ""}${API_PATH.GET_USER_PROFILE_BY_ID(
      userId
    )}`;
    const res = await doGet(url, {
      headers: { Authorization: `Token ${token}` },
      timeout: 10000,
    });
    return !!res && res.status >= 200 && res.status < 300;
  } catch (_e) {
    return false;
  }
}

/**
 * Flush EVERY cross-account cache. Enumerated so the PR's security section can
 * cite exactly what is wiped:
 *   - react-query: queryClient.clear()  → drops all cached queries/mutations.
 *   - redux (persisted): remove "persist:root" (+ persistor.purge() defensively)
 *       → the store rehydrates EMPTY on the reload that follows.
 *   - redux (in-memory) & all React contexts → recreated by the hard reload.
 *   - account-scoped localStorage keys (AITokenLeft, show_walkthrough).
 *   - account-scoped sessionStorage keys (referralData).
 * Returns the list of storage keys it removed (for tests / audit).
 */
export function flushSessionCaches({
  queryClient,
  persistor,
  storage,
  session,
} = {}) {
  const removed = [];

  if (queryClient && typeof queryClient.clear === "function") {
    queryClient.clear();
  }
  if (persistor && typeof persistor.purge === "function") {
    try {
      persistor.purge();
    } catch (_e) {
      /* the direct removeItem below is the real guarantee */
    }
  }
  if (storage) {
    ACCOUNT_SCOPED_LS_KEYS.forEach((k) => {
      try {
        storage.removeItem(k);
        removed.push(k);
      } catch (_e) {
        /* ignore */
      }
    });
  }
  if (session) {
    ACCOUNT_SCOPED_SS_KEYS.forEach((k) => {
      try {
        session.removeItem(k);
        removed.push(k);
      } catch (_e) {
        /* ignore */
      }
    });
  }
  return removed;
}

/** Write the target account's credential into the app's single-session slot. */
export function setActiveSession({ token, user }, storage) {
  if (!storage || !token) return;
  storage.setItem(KEYCLOAK_KEY, JSON.stringify({ token, user }));
}

/**
 * Seamless switch to an already-validated account: flush all caches, install the
 * target's keyCloak, then HARD-navigate to that account's gallery (full reload =
 * clean recreate of store/query-client/contexts — the anti-bleed guarantee).
 */
export function switchToAccount({
  account,
  queryClient,
  persistor,
  storage = typeof window !== "undefined" ? window.localStorage : null,
  session = typeof window !== "undefined" ? window.sessionStorage : null,
  assign = (url) => {
    window.location.assign(url);
  },
} = {}) {
  if (!account || !account.token || account.id == null) return false;
  flushSessionCaches({ queryClient, persistor, storage, session });
  setActiveSession(
    { token: account.token, user: account.user || { id: account.id } },
    storage
  );
  assign(`/gallery/${account.id}`);
  return true;
}

/**
 * Build a registry entry for the CURRENTLY signed-in account from its live keyCloak
 * + redux userProfile. Pure — no side effects — so "remember current" is testable.
 */
export function currentAccountEntry(keyCloak, userProfile) {
  if (!keyCloak || !keyCloak.token) return null;
  const user = keyCloak.user || {};
  const id = user.id != null ? user.id : userProfile && userProfile.id;
  if (id == null) return null;
  const name =
    (userProfile && (userProfile.name || userProfile.full_name)) ||
    user.name ||
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
    (userProfile && userProfile.email) ||
    user.email ||
    "";
  return {
    id,
    email: (userProfile && userProfile.email) || user.email || "",
    name: name || undefined,
    avatar: (userProfile && userProfile.profile_image) || user.profile_image || "",
    token: keyCloak.token,
  };
}

/**
 * ISP-9999 add-account fix: complete the Account Switcher's "Add account"
 * flow (Login/index.jsx, addAccount=1) once the NEW account's login has
 * already succeeded. This is the registry+switch counterpart to the
 * "Switch to <account>" row's handleSwitch() — same guarantee, same
 * mechanism, different entry point:
 *
 *  1. Remember the PREVIOUS account (the one signed in before this login
 *     started) so it is never lost — even if the caller can't rely on the
 *     dropdown's own mount-time registration having already run.
 *  2. Remember the NEW account.
 *  3. Hand off via switchToAccount() — the SAME anti-bleed hard-reload used
 *     everywhere else a session changes. A bare SPA nav() here would leave
 *     the previous account's redux/react-query state mounted under the new
 *     account's token (the exact bleed switchToAccount exists to prevent).
 *
 * `previous` may be null (e.g. the session expired between clicking "Add
 * account" and submitting) — that account is simply not (re-)remembered.
 * Never throws; a registry-write failure degrades to "not remembered" (same
 * best-effort posture as accountRegistry.js), it never blocks the switch.
 */
export function completeAddAccountLogin({
  previous,
  next,
  queryClient,
  persistor,
  upsert = upsertAccount,
  buildEntry = currentAccountEntry,
  storage,
  session,
  assign,
} = {}) {
  if (previous && previous.token && previous.id != null) {
    const prevEntry = buildEntry(
      { token: previous.token, user: previous.user || { id: previous.id } },
      null
    );
    if (prevEntry) {
      try {
        upsert(prevEntry);
      } catch (_e) {
        /* best-effort remember — never blocks the switch */
      }
    }
  }

  if (next && next.token) {
    const nextEntry = buildEntry(
      { token: next.token, user: next.user || { id: next.id } },
      null
    );
    if (nextEntry) {
      try {
        upsert(nextEntry);
      } catch (_e) {
        /* best-effort remember — never blocks the switch */
      }
    }
  }

  return switchToAccount({
    account: next,
    queryClient,
    persistor,
    ...(storage !== undefined ? { storage } : {}),
    ...(session !== undefined ? { session } : {}),
    ...(assign !== undefined ? { assign } : {}),
  });
}
